import { Investment, RequestOptions, TApiResponse, TPager, } from "@/types";

import http from "@/http";
import {PaymentParams} from "@/features/applications/repositories/licences-repository.ts";


/**
 * Download a per-control-number PDF for the customer's own bill and trigger a
 * browser save.
 *
 * `invoice` — the control-number slip: control number, generated/expiry dates,
 *             customer + bill details (any live bill: PENDING/PROCESSING/PAID).
 * `receipt` — payment confirmation (PAID bills only).
 *
 * The backend streams `application/pdf`; on error it returns `{ message }` JSON
 * which arrives here as a Blob and is unwrapped so callers can show it.
 */
export async function downloadPaymentDocument(
    controlNumber: string,
    type: "invoice" | "receipt"
) {
    try {
        const response = await http.get(
            `/payments/${encodeURIComponent(controlNumber)}/${type}`,
            { responseType: "blob" }
        );

        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement("a");
        anchor.href = url;
        anchor.download = `ZCT-${type === "receipt" ? "Receipt" : "Invoice"}-${controlNumber}.pdf`;
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.URL.revokeObjectURL(url);
    } catch (error: any) {
        const data = error?.response?.data;
        if (data instanceof Blob) {
            try {
                const parsed = JSON.parse(await data.text());
                if (parsed?.message) throw new Error(parsed.message);
            } catch (e: any) {
                if (e instanceof Error && e.message) throw e;
            }
        }
        throw new Error(error?.message || "Failed to download document");
    }
}

export type MobileProvider = 'MPESA' | 'TIGOPESA' | 'AIRTEL_MONEY' | 'HALOPESA' | 'EAZYPESA' | 'TPESA';
export type ZanCardType = 'VISA' | 'MASTERCARD' | 'AMEX' | 'UNIONPAY';
export type ZanPaymentStatus = 'PENDING' | 'PROCESSING' | 'PAID' | 'FAILED' | 'EXPIRED';

// ZanMalipo customer-identification type code (§2 of the field reference).
// 1=NIN, 2=Zanzibar ID, 3=Driver's License, 4=TIN, 5=Custom, 6=Wallet
export type ZanCustIdType = '1' | '2' | '3' | '4' | '5' | '6';

export interface ZanMalipoResult {
    controlNumber: string;
    zanmalipoReqId: string;
    amount: number;
    currency: string;
    status: ZanPaymentStatus;
    paymentMethod: 'MOBILE_MONEY' | 'CARD';
    transactionReference: string;
    createdAt: string;
    usedPayChannel: string | null;
    payRefId: string | null;
}

export interface UssdPushResult extends ZanMalipoResult {
    pushInitiated: boolean;
    pushMessage: string;
    pushConversationId: string | null;
}

export interface ZanCustomerFields {
    /**
     * The owning licence's `applicationId` (e.g. "LICE-14830006"), NOT its numeric id.
     *
     * The backend stores this on the Payment and `LicenseService.attachBill` matches on it to
     * put the control number, fee and payment status on the licence. It was never sent, so
     * every bill raised from the customer portal was orphaned from its application and those
     * three fields rendered blank on both portals.
     */
    applicationId?: string;
    custName?: string;
    custId?: string;
    custIdTyp?: ZanCustIdType;
    custTin?: string;
    custAccnt?: string;
    custEmail?: string;
    billDesc?: string;
    gfsCode?: string;
}

export interface ControlNumberInput extends ZanCustomerFields {
    amount: number;
    currency?: string;
    phoneNumber: string;
    provider: MobileProvider;
}

export interface CardPaymentInput extends ZanCustomerFields {
    amount: number;
    currency?: string;
    cardType?: ZanCardType;
    cardNumber: string;
    cardHolderName: string;
    cardExpiryMonth: string;
    cardExpiryYear: string;
    cvv: string;
    phoneNumber?: string;
    email?: string;
}

// Convert any local input (07XXXXXXXX, +255…, 255…) to the 12-digit form
// ZanMalipo requires. Returns empty string if it can't form a valid number.
export function normalizeTzPhone(raw: string | undefined | null): string {
    if (!raw) return '';
    const digits = String(raw).replace(/\D/g, '');
    if (digits.length === 12 && digits.startsWith('255')) return digits;
    if (digits.length === 10 && digits.startsWith('0')) return '255' + digits.slice(1);
    if (digits.length === 9) return '255' + digits;
    return digits;
}

export function isValidTzPhone(value: string | undefined | null): boolean {
    return !!value && /^255[0-9]{9}$/.test(value);
}

export async function getLicenseCategories() {
    const response = await http.get<TApiResponse<any>>("/license-categories", {
        params: {
            page:1,
            size: 10
        }
    })
    return response.data
}

export async function getLicences(
    search?: string, 
    pager?: TPager, 
    filter?: Record<string, any>
) {
    const response = await http.get<TApiResponse<any>>("/licence", {
        params: {
            "search": search,
            ...pager,
            ...filter
        }
    })
    return response.data
}

export async function gePayments(
    search?: string, 
    pager?: TPager, 
    filter?: Record<string, any>
) {
    const response = await http.get<TApiResponse<any>>("/payments", {
        params: {
            "search": search,
            ...pager,
            ...filter
        }
    })
    return response.data
}


export async function createLicence(input: any) {
    const response = await http.post<TApiResponse<null>>(
        "/licence", input
    )
    return response.data
}

// export async function generatPaymentControlNumber() {
//     const response = await http.post<TApiResponse<null>>(
//         "/payments/mobile/control-number", {
//             "amount": 1000,
//             "currency": "TZS",
//             "phoneNumber": "255123456789",
//             "provider": "MPESA"
//         }
//     )
//     return response.data
// }
//
// // export async function paymentControlNumber(controlNumber: string) {
// //     const response = await http.post<TApiResponse<null>>(
// //         `/payments/mobile/process/${controlNumber}`, {
// //
// //         }
// //     )
// //     return response.data
// // }
//
// export async function paymentControlNumber({ controlNumber, amount }: PaymentParams) {
//     const response = await http.post<TApiResponse<null>>(
//         `/payments/mobile/process/${controlNumber}`,
//         {
//             amount
//         }
//     )
//     return response.data
//
// }

export async function paymentControlNumber({ controlNumber, amount, paymentCategory }: PaymentParams) {
    const response = await http.post<TApiResponse<null>>(
        `/payments/mobile/process/${controlNumber}`,
        {
            amount,
            paymentCategory,
        }
    );
    return response.data;
}

// ZanMalipo field-length caps (§2, §3 of the field reference)
const ZAN_LIMITS = {
    custName: 200,
    custId: 50,
    custTin: 15,
    custAccnt: 50,
    custEmail: 30,
    billDesc: 500,
} as const;

function clamp(value: string | undefined, max: number): string | undefined {
    if (value === undefined || value === null) return value;
    const trimmed = String(value).trim();
    if (!trimmed) return undefined;
    return trimmed.length > max ? trimmed.slice(0, max) : trimmed;
}

function clampCustomerFields<T extends ZanCustomerFields>(input: T): T {
    return {
        ...input,
        custName: clamp(input.custName, ZAN_LIMITS.custName),
        custId: clamp(input.custId, ZAN_LIMITS.custId),
        custTin: clamp(input.custTin, ZAN_LIMITS.custTin),
        custAccnt: clamp(input.custAccnt, ZAN_LIMITS.custAccnt),
        custEmail: clamp(input.custEmail, ZAN_LIMITS.custEmail),
        billDesc: clamp(input.billDesc, ZAN_LIMITS.billDesc),
    };
}

export async function generateControlNumber(input: ControlNumberInput) {
    const phoneNumber = normalizeTzPhone(input.phoneNumber);
    if (!isValidTzPhone(phoneNumber)) {
        throw new Error('Phone number must be 12 digits starting with country code, e.g. 255712345678');
    }
    const response = await http.post<TApiResponse<ZanMalipoResult>>(
        '/payments/mobile/control-number',
        { currency: 'TZS', ...clampCustomerFields(input), phoneNumber }
    );
    return response.data;
}

export async function submitUssdPush(input: ControlNumberInput) {
    const phoneNumber = normalizeTzPhone(input.phoneNumber);
    if (!isValidTzPhone(phoneNumber)) {
        throw new Error('Phone number must be 12 digits starting with country code, e.g. 255712345678');
    }
    const response = await http.post<TApiResponse<UssdPushResult>>(
        '/payments/mobile/ussd-push',
        { currency: 'TZS', ...clampCustomerFields(input), phoneNumber }
    );
    return response.data;
}

export async function submitCardPayment(input: CardPaymentInput) {
    const payload: CardPaymentInput = { currency: 'TZS', ...clampCustomerFields(input) };
    if (payload.phoneNumber) payload.phoneNumber = normalizeTzPhone(payload.phoneNumber);
    const response = await http.post<TApiResponse<ZanMalipoResult>>(
        '/payments/card',
        payload
    );
    return response.data;
}

export async function getPaymentStatus(controlNumber: string) {
    const response = await http.get<TApiResponse<ZanMalipoResult>>(
        `/payments/status/${controlNumber}`
    );
    return response.data;
}

/**
 * Reuse (re-activate) an existing control number — ZanMalipo §4.3.
 *
 * The customer uses this when a control number has expired or was cancelled and
 * they still want to pay. The backend looks the bill up by `billCntrNum`, so
 * that is the only required field; everything else falls back to the stored
 * bill. `reuseReasn` is mandatory to ZanMalipo whenever the amount decreased or
 * the bill has not expired yet, so we always send one.
 */
export interface ReuseControlNumberInput {
    billCntrNum: string;
    reuseReasn?: string;
    newAmount?: number;
    currency?: string;
    billDesc?: string;
    custName?: string;
    custEmail?: string;
}

export async function reuseControlNumber(input: ReuseControlNumberInput) {
    const response = await http.post<TApiResponse<ZanMalipoResult>>(
        '/payments/control-number/reuse',
        {
            reuseReasn: 'Customer re-initiated payment',
            currency: 'TZS',
            ...input,
        }
    );
    return response.data;
}

export async function createInvestment(input: any) {
    const response = await http.post<TApiResponse<null>>(
        "/investment", input
    )
    return response.data
}

export async function getLicenceDetails(applicationId: string) {
    const response = await http.get<TApiResponse<any>>(`/licence/${applicationId}`)
    return response.data
}


export async function getInvestiments(options: RequestOptions) {
    const response = await http.get<TApiResponse<Investment[]>>("/investment", {
        params: {
            "search": options.search,
            ...options.pager,
            ...options.filter
        }
    })
    return response.data
}



export async function getInvestimentDetails(applicationId: string) {
    const response = await http.get<TApiResponse<Investment>>(`/investment/${applicationId}`)
    return response.data
}



export async function getLocations(
    search?: string, 
    pager?: TPager, 
    filter?: Record<string, any>
) {
    const response = await http.get<TApiResponse<any>>("/countries/1/zones", {
        params: {
            "search": search,
            ...pager,
            ...filter
        }
    })
    return response.data
}

/* ------------------------------------------------------------------------- *
 * Approval workflow
 *
 * Shapes below mirror `UnifiedApprovalDTO` (and the DTOs it nests) in the API:
 *   application/dto/UnifiedApprovalDTO.java
 *   application/utiil/common/dto/approval/{ApprovalStatusDTO,CommitteeFeedbackDTO}.java
 *   Inspection/InspectionDTO.java
 * Everything is optional because the backend omits/nulls whole phases until the
 * corresponding stage of the workflow has actually happened.
 * ------------------------------------------------------------------------- */

export interface ApprovalPhaseStep {
    name?: string | null;
    status?: string | null;
    assignedTo?: string | null;
    assignedAt?: string | null;
    completedAt?: string | null;
    completedBy?: string | null;
    stepNumber?: number | null;
}

export interface ApprovalInspectionArea {
    id?: number | null;
    name?: string | null;
    passed?: boolean | null;
    comments?: string | null;
    findings?: string[] | null;
}

export interface ApprovalInspection {
    id?: number | null;
    applicationId?: number | null;
    inspectorIds?: number[] | null;
    type?: string | null;
    inspectorNames?: string[] | null;
    scheduledDate?: string | null;
    completedDate?: string | null;
    status?: string | null;
    areasOfInspection?: ApprovalInspectionArea[] | null;
    location?: string | null;
    description?: string | null;
    createdBy?: string | null;
    createdAt?: string | null;
    feedback?: string | null;
    violations?: string[] | null;
    recommendations?: string[] | null;
}

export interface ApprovalCommitteeFeedback {
    id?: number | null;
    licenseId?: number | null;
    feedbackType?: string | null;
    comment?: string | null;
    createdBy?: string | null;
    discussionDate?: string | null;
    createdAt?: string | null;
    requiresResponse?: boolean | null;
    status?: string | null;
    category?: string | null;
    priority?: 'HIGH' | 'MEDIUM' | 'LOW' | null;
}

export interface ApprovalLegalPhase {
    status?: string | null;
    simpleStatus?: string | null;
    comment?: string | null;
    reviewedBy?: string | null;
    reviewDate?: string | null;
    findings?: string[] | null;
    steps?: ApprovalPhaseStep[] | null;
}

export interface ApprovalInspectionFinalDecision {
    status?: string | null;
    simpleStatus?: string | null;
    decidedBy?: string | null;
    decisionDate?: string | null;
    comment?: string | null;
}

export interface ApprovalInspectionPhase {
    status?: string | null;
    simpleStatus?: string | null;
    comment?: string | null;
    inspectionDate?: string | null;
    inspection?: ApprovalInspection | null;
    committeeFeedbacks?: ApprovalCommitteeFeedback[] | null;
    steps?: ApprovalPhaseStep[] | null;
    finalDecision?: ApprovalInspectionFinalDecision | null;
}

export interface ApprovalDocumentFinding {
    url?: string | null;
    comments?: string | null;
    requiresFollowUp?: boolean | null;
    approved?: boolean | null;
    applicationId?: number | null;
}

export interface ApprovalDocumentPhase {
    status?: string | null;
    simpleStatus?: string | null;
    documentFindings?: ApprovalDocumentFinding[] | null;
    allDocumentsApproved?: boolean | null;
    steps?: ApprovalPhaseStep[] | null;
}

export interface ApprovalFinalPhase {
    status?: string | null;
    simpleStatus?: string | null;
    comment?: string | null;
    decidedBy?: string | null;
    decisionDate?: string | null;
    conditions?: string[] | null;
    isFullyApproved?: boolean | null;
    steps?: ApprovalPhaseStep[] | null;
}

export interface UnifiedApproval {
    applicationId?: number | null;
    applicationNumber?: string | null;
    applicationType?: string | null;
    currentStatus?: string | null;
    simpleStatus?: string | null;
    completedPhases?: number | null;
    totalPhases?: number | null;
    legalPhase?: ApprovalLegalPhase | null;
    inspectionPhase?: ApprovalInspectionPhase | null;
    documentPhase?: ApprovalDocumentPhase | null;
    finalApprovalPhase?: ApprovalFinalPhase | null;
}

export async function getLicenceApprovalStatus(licenceId: string) {
    const response = await http.get<TApiResponse<UnifiedApproval>>(
        `/licence/approvals/${licenceId}/approval-status`
    )
    return response.data
}

export async function getInvestmentApprovalStatus(investmentId: string) {
    const response = await http.get<TApiResponse<UnifiedApproval>>(
        `/approvals/${investmentId}/approval-status`
    )
    return response.data
}

/**
 * Resubmission after a phase returned the application for correction.
 *
 * Both endpoints require the CUSTOMER role and 409 CONFLICT (with a message
 * explaining the current state) when the application is not actually in
 * RETURNED_FOR_CORRECTION — see ApprovalWorkflowGuard.requirePhaseOrder.
 * On success the application re-enters the phase that returned it.
 */
export async function resubmitLicence(licenceId: string) {
    const response = await http.post<TApiResponse<any>>(
        `/licence/approvals/${licenceId}/resubmit`
    )
    return response.data
}

export async function resubmitInvestment(investmentId: string) {
    const response = await http.post<TApiResponse<Investment>>(
        `/approvals/${investmentId}/resubmit`
    )
    return response.data
}

/* ------------------------------------------------------------------------- *
 * Editing an existing application
 *
 * PUT /licence/{id}      body LicenseRequestDTO      (LicenseController.updateLicense)
 * PUT /investment/{id}   body ApplicationRequestDto  (investment controller.updateApplication)
 *
 * Both bodies below mirror the Java DTOs field for field. Anything the DTO does
 * not declare is dropped by Jackson without a word, so the edit would look like
 * it "didn't save" — hence the explicit shapes rather than passing the raw form
 * values through.
 *
 * LicenseRequestDTO is FLAT: no `businessDetails` wrapper. The server splits it
 * back apart in LicenseMapper.toLicenseDTO and then, in
 * LicenseService.updateLicenseFields, REPLACES the businessDetail, address,
 * contact and employment blocks wholesale. A field that is left out of the body
 * is therefore not "left alone", it is nulled — so the caller has to echo the
 * values it is not editing back to the server. See the payload builders in
 * pages/edit/.
 * ------------------------------------------------------------------------- */

/** A licence sub-category as LicenseRequestDTO.ServiceRequest declares it. */
export interface LicenceServiceRequest {
    id: number;
    label?: string;
}

/** Exactly the LicenseRequestDTO fields this portal is able to edit or preserve. */
export interface LicenceUpdatePayload {
    // Business details
    projectName?: string;
    physicalLocation?: string;
    businessName?: string;
    dateIssues?: string;
    previousLicenseNumber?: string;
    previousLicenseDateIssues?: string;
    nameOfWhichCurrentLicenseIsHeld?: string;
    existingBuildings?: string;

    // Principal business location
    zone?: string;
    region?: string;
    district?: string;
    municipal?: string;
    street?: string;
    principalBusinessLocationPostCode?: string;
    principalBusinessLocationPoBox?: string;

    // Contact
    principalBusinessLocationEmail?: string;
    principalBusinessLocationPhoneNumber?: string;
    principalBusinessWebsite?: string;

    // Category + services
    selectedCategory?: number;
    selectedServices?: LicenceServiceRequest[];

    // Employment — echoed back untouched, because updateLicenseFields replaces
    // the whole employmentDetails block on every update.
    zanzibarisManagmentMale?: number;
    zanzibarisManagmentFemale?: number;
    zanzibarisSkilledMale?: number;
    zanzibarisSkilledFemale?: number;
    zanzibarisUnskilledMale?: number;
    zanzibarisUnskilledFemale?: number;
    tanzaniaMainlandersManagmentMale?: number;
    tanzaniaMainlandersManagmentFemale?: number;
    tanzaniaMainlandersSkilledMale?: number;
    tanzaniaMainlandersSkilledFemale?: number;
    tanzaniaMainlandersUnskilledMale?: number;
    tanzaniaMainlandersUnskilledFemale?: number;
    foreignersManagmentMale?: number;
    foreignersManagmentFemale?: number;
    foreignersSkilledMale?: number;
    foreignersSkilledFemale?: number;
    foreignersUnskilledMale?: number;
    foreignersUnskilledFemale?: number;
}

/**
 * Save an edited licence application.
 *
 * The API answers 403 when the signed-in applicant is not the one who created
 * the application (LicenseService.validateLicenceOwnership) and 409 once it has
 * been approved, rejected or expired (validateLicenseUpdateAllowed). Both carry
 * a `{ message }` the applicant can act on, so callers surface it verbatim.
 */
export async function updateLicence(id: string | number, input: LicenceUpdatePayload) {
    const response = await http.put<TApiResponse<any>>(
        `/licence/${id}`, input
    )
    return response.data
}

/** CapitalInvestmentDto.AmountContribution — a numeric amount with its currency. */
export interface InvestmentAmount {
    amount: number;
    currency?: string;
}

/** ApplicationRequestDto and the DTOs it nests. Every leaf here is a String on
 *  the Java side except numberOfRooms, projectActivities and the amounts. */
export interface InvestmentUpdatePayload {
    businessDetails?: {
        projectName?: string;
        projectNumber?: string;
        dateIssued?: string;
        businessName?: string;
        previousLicenseNumber?: string;
        previousLicenseDateIssued?: string;
        currentLicenseName?: string;
        zone?: string;
        region?: string;
        district?: string;
        street?: string;
        postCode?: string;
        poBox?: string;
        projectType?: string;
        projectActivities?: string[];
        numberOfRooms?: number;
    };
    capitalInvestment?: {
        companyOperatingProject?: string;
        estimatedInvestmentAmount?: InvestmentAmount;
        currency?: string;
        personalContribution?: InvestmentAmount;
        loanAmount?: InvestmentAmount;
        institutionProvidingLoan?: string;
        bankName?: string;
        bankAddress?: string;
        shareholders?: Array<{
            salutation?: string;
            firstName?: string;
            middleName?: string;
            lastName?: string;
            nationality?: string;
            numberOfShares?: string;
        }>;
    };
    construction?: {
        landSizeInHectares?: string;
        existingBuildingsUsed?: string;
        constructionCompletionTime?: string;
        projectLifespanYears?: string;
        environmentalManagementPlans?: string;
    };
    workforce?: {
        localPermanentEmployees?: string;
        localTemporaryEmployees?: string;
        foreignPermanentEmployees?: string;
        foreignTemporaryEmployees?: string;
    };
}

/**
 * Save an edited investment application.
 *
 * Addressed by the human-facing applicationId (e.g. ZCT-00037402), the same key
 * the detail endpoint uses — not the numeric id.
 */
export async function updateInvestment(applicationId: string, input: InvestmentUpdatePayload) {
    const response = await http.put<TApiResponse<Investment>>(
        `/investment/${applicationId}`, input
    )
    return response.data
}
